// The access-pending screen and the gate that decides to show it. Not authored surface: the box
// restores this file from the template on every sync, and `isAgentAppWritable` refuses a write under
// `src/lib/platform/`.
//
// The DECISION lives next door in `access.ts` and is a pure function of two values; what is here is
// only the rendering of it, plus the three things about this particular screen that would otherwise
// fail in silence — it stamps the readiness marker itself, it owns its own refresh, and it offers a
// way out. Styled with stock utility classes and no imports from `src/components/ui/`: the app's own
// component kit is the author's to delete, and the one screen that explains an empty product must not
// break with it.

import { type ReactNode, useEffect, useState } from 'react';
import { type AppAccess, accessGateVerdict } from './access';
import { refreshSession } from './auth';
import { useSession } from './hooks';
import { leaveAfterSignOut } from './routing';

/** How often the screen re-asks while it is open. A grant is a human action somebody else takes in
 *  another window, so the wait is measured in seconds and the cost is one request per interval. */
const RECHECK_MS = 15_000;

/**
 * THE READINESS MARKER, STAMPED BY THIS SCREEN ITSELF, because the authored `App.tsx` that normally
 * stamps it never mounts underneath. `data-app-ready` on `#root` is the platform's only witness that
 * a document finished rendering: without it every observer reads this app as never-mounted and charges
 * an honest access state as a boot crash. Never removed — the document IS ready — and the authored
 * app stamps it again for itself the moment a grant lets it mount.
 */
function useStampAppReady(): void {
  useEffect(() => {
    document.getElementById('root')?.setAttribute('data-app-ready', '1');
  }, []);
}

/**
 * THIS SCREEN OWNS ITS OWN REFRESH, and that is not belt-and-braces. The auth client's return-to-page
 * re-probe deliberately SKIPS an authed session — an authed session needs no confirming, and asking
 * again on every tab switch is a request per focus for nothing. This person is the single exception:
 * they are signed in and waiting on a fact only the server holds, so without this the moment somebody
 * grants them a role changes nothing at all until they reload the page by hand.
 */
function useRecheckAccess(): void {
  useEffect(() => {
    const recheck = (): void => {
      void refreshSession();
    };
    const timer = setInterval(recheck, RECHECK_MS);
    window.addEventListener('focus', recheck);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', recheck);
    };
  }, []);
}

/**
 * OWNER-VOICE, and every word of it is chosen against a reader who has never heard of this platform.
 * It says who they are signed in as (so a person with two accounts can see which one is waiting), it
 * says the roles here are the owner's to give (so "pending" is a fact about a decision somebody else
 * makes, not a fault of theirs), and it names no console, no tool and no vendor.
 */
function AccessPending({ email }: { email: string }): ReactNode {
  const [checking, setChecking] = useState(false);
  useStampAppReady();
  useRecheckAccess();

  const checkAgain = (): void => {
    setChecking(true);
    void refreshSession().finally(() => {
      setChecking(false);
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-6 dark:bg-neutral-950">
      <div className="w-full max-w-md rounded-lg border border-neutral-200 bg-white p-8 text-neutral-900 shadow-sm dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100">
        <h1 className="text-lg font-semibold">Your access is pending</h1>
        <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
          {email.length > 0 ? `You're signed in as ${email}. ` : "You're signed in. "}
          This app's roles are granted by its owner, and yours hasn't been granted yet.
        </p>
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          This page checks again on its own — you can leave it open.
        </p>
        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            onClick={checkAgain}
            disabled={checking}
            className="rounded-md bg-neutral-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-60 dark:bg-neutral-100 dark:text-neutral-900"
          >
            {checking ? 'Checking…' : 'Check again'}
          </button>
          <button
            type="button"
            onClick={() => {
              void leaveAfterSignOut();
            }}
            className="rounded-md px-3 py-2 text-sm font-medium text-neutral-600 underline-offset-4 hover:underline dark:text-neutral-400"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

/** The subscribing half. Split out so the branch above it can answer without a session at all. */
function SessionGate({ access, children }: { access: AppAccess; children: ReactNode }): ReactNode {
  const session = useSession();
  const verdict = accessGateVerdict(access, session);
  if (verdict === 'wait') return null;
  if (verdict === 'app') return children;
  return <AccessPending email={session.user?.email ?? ''} />;
}

/**
 * What `src/main.tsx` wraps the authored `<App/>` in.
 *
 * AN APP WITH NO ACCOUNTS NEVER SUBSCRIBES. The generated fact is absent — not false — for a model
 * with no auth, exactly as the server emits no `roles.ts` for one, and answering that here rather than
 * inside `SessionGate` is what keeps such an app from acquiring a `get-session` probe it never had.
 */
export function AccessGate({ access, children }: { access: AppAccess | undefined; children: ReactNode }): ReactNode {
  if (access === undefined) return children;
  return <SessionGate access={access}>{children}</SessionGate>;
}
